/**
 * The typed HTTP client — the ONLY place `apps/web` speaks to the API.
 *
 * It owns three things that nothing else may duplicate:
 *
 * 1. **The session-token lifecycle** (`spec.md` §9.2). The token lives here, in memory,
 *    and is attached as `Authorization: Bearer` to every authenticated call. There are no
 *    cookies anywhere in this application (§9.3), so {@link ApiClient} sends
 *    `credentials: 'omit'` on every request — a `Set-Cookie` we never asked for cannot
 *    then be replayed by the media element, which is the whole point of the URL-token
 *    design.
 *
 *    Refresh is **checked on the request path**, not driven by a timer: `POST
 *    /api/auth/refresh` is issued when the clock has passed 80% of the token's TTL
 *    (§9.2, last bullet). A `setTimeout` would be the obvious alternative and is wrong
 *    here — a minimised Telegram WebView on Android is free to throttle or coalesce
 *    timers, so a timer-driven refresh fires late exactly when a listening session has
 *    been running for hours, which is the case it exists for. A check on the next real
 *    request cannot be late by construction.
 *
 *    A `SESSION_EXPIRED` answer is retried **once**, by re-exchanging `initData`
 *    (§6.2: "Client must re-run `/api/auth/telegram`"). A second failure surfaces.
 *
 * 2. **Media-URL batching and byte-stability** (§9.4 "Caching interaction", §9.5, §10.8).
 *    {@link ApiClient.ensureMediaUrls} mints the URL for the current *and* the next track
 *    in one call, well before auto-advance needs it, and caches the exact string. Once a
 *    URL is minted it is returned **verbatim** for as long as it is valid: the client-side
 *    prefetch issues `fetch(url, { headers: { Range: … } })` against the same string the
 *    `<audio>` element will later request, and a single differing byte turns that warm
 *    range into a cache miss and doubles the bandwidth of every track.
 *
 * 3. **Typed failures.** Every failure — HTTP, transport, timeout, malformed body — is an
 *    {@link ApiError}, never a thrown string and never a bare `TypeError` from `fetch`.
 *    `ApiError.toFailure()` produces a plain, serialisable {@link ApiFailure} carrying the
 *    closed-set code, a Russian sentence for the UI, and the mono catalogue code that
 *    `design-direction.md` §8.9 prints on the `NEEDLE SKIP` plate. Store state holds the
 *    *failure*, never the `Error` instance, so every slice stays JSON-serialisable.
 *
 * Response validation runs the `@bl/shared` zod schemas in development and is skipped in
 * production (`validate`, defaulting to `import.meta.env.DEV`). The error envelope is the
 * one exception: it is validated in **both** modes, because an unrecognised `error.code`
 * must degrade to a real message rather than reach the UI as `undefined`.
 *
 * `zod` is deliberately not imported here — it is not a dependency of `@bl/web`. The
 * schemas arrive as opaque {@link ResponseValidator}s through `@bl/shared`.
 */

import {
  ArtistDetailResponseSchema,
  ArtistsResponseSchema,
  AuthRefreshResponseSchema,
  AuthTelegramResponseSchema,
  CatalogueResponseSchema,
  ERROR_CODES,
  ErrSchema,
  FavoriteAddedResponseSchema,
  FavoriteRemovedResponseSchema,
  FavoritesResponseSchema,
  HistoryResponseSchema,
  MediaTokenResponseSchema,
  PAD_MEDIA_PREFIX,
  PadKitResponseSchema,
  PadSoundsResponseSchema,
  PlaybackPutResponseSchema,
  PlaybackResponseSchema,
  PlayCreateResponseSchema,
  PlaylistAddTracksResponseSchema,
  PlaylistCreateResponseSchema,
  PlaylistDetailResponseSchema,
  PlaylistPatchResponseSchema,
  PlaylistRemoveTrackResponseSchema,
  PlaylistReorderResponseSchema,
  PlaylistsResponseSchema,
  QueueHeaderSchema,
  QueueResponseSchema,
  SearchResponseSchema,
  ShareTrackResponseSchema,
  SuggestResponseSchema,
  TagDetailResponseSchema,
  TagsResponseSchema,
  TrackDetailSchema,
  TrackPeaksResponseSchema,
} from '@bl/shared';
import type {
  Ack,
  ArtistDetailResponse,
  ArtistsResponse,
  AuthRefreshResponse,
  AuthTelegramResponse,
  CatalogueResponse,
  CatalogueSort,
  ErrorCode,
  FavoriteAddedResponse,
  FavoriteRemovedResponse,
  FavoritesResponse,
  HistoryResponse,
  IndexSort,
  LibrarySort,
  MediaTokenKind,
  MediaTokenResponse,
  PlaybackPutRequest,
  PlaybackPutResponse,
  PadKitPutRequest,
  PadKitResponse,
  PadSoundsResponse,
  PlaybackResponse,
  PlayCreateRequest,
  PlayCreateResponse,
  PlaylistAddTracksRequest,
  PlaylistAddTracksResponse,
  PlaylistCreateRequest,
  PlaylistCreateResponse,
  PlaylistDetailResponse,
  PlaylistPatchRequest,
  PlaylistPatchResponse,
  PlaylistReorderRequest,
  PlaylistReorderResponse,
  PlaylistsResponse,
  QueueAddItemsRequest,
  QueueHeader,
  QueueReorderRequest,
  QueueReplaceRequest,
  QueueResponse,
  SearchGroupName,
  SearchResponse,
  ServerInfo,
  SessionUser,
  ShareTrackRequest,
  ShareTrackResponse,
  SuggestResponse,
  TagDetailResponse,
  TagsResponse,
  TrackDetail,
  TrackPeaksResponse,
} from '@bl/shared';

import { telegram } from './telegram';

/* ────────────────────────────────────────────────────────────────────────────
 * Failure taxonomy
 * ──────────────────────────────────────────────────────────────────────────── */

/**
 * Failures that never come from the server, so they cannot live in `spec.md` §6.2's
 * closed set. They are kept in a separate, equally closed list so that
 * {@link USER_MESSAGE_RU} and {@link displayCode} are total over every code the UI can
 * ever be handed.
 */
export const CLIENT_ERROR_CODES = [
  /** `fetch` rejected: no route to the host, DNS, TLS, a dropped WebView connection. */
  'NETWORK_FAILED',
  /** No answer within the request deadline. */
  'TIMEOUT',
  /** The body was not the `{ok,data}` envelope, or failed its zod schema. */
  'BAD_RESPONSE',
  /** The caller aborted (a superseded search, an unmounted screen). Never user-visible. */
  'ABORTED',
  /** No `initData`: a keyboard-button or inline launch, or a plain browser. */
  'NO_INIT_DATA',
] as const;

export type ClientErrorCode = (typeof CLIENT_ERROR_CODES)[number];

/** Every code the UI may be asked to render. */
export type ApiFailureCode = ErrorCode | ClientErrorCode;

/** The full code list, server codes first, in the order the two sources declare them. */
export const ALL_FAILURE_CODES: readonly ApiFailureCode[] = [...ERROR_CODES, ...CLIENT_ERROR_CODES];

/**
 * One plain Russian sentence per code.
 *
 * `design-direction.md` §8.9: the error plate carries a mono catalogue code and "one
 * plain sentence of UI `--fs-15`". These are those sentences. They never name an HTTP
 * status, never say "ошибка 500", and never blame the user.
 */
export const USER_MESSAGE_RU: Readonly<Record<ApiFailureCode, string>> = Object.freeze({
  BAD_REQUEST: 'Запрос не прошёл проверку. Обновите приложение.',
  BAD_CURSOR: 'Список сместился. Откройте его заново.',
  NO_SESSION: 'Сеанс не начат. Откройте приложение из Telegram.',
  SESSION_EXPIRED: 'Сеанс истёк. Обновляем доступ.',
  BAD_SESSION: 'Доступ не подтверждён. Откройте приложение заново.',
  INITDATA_BAD_HASH: 'Telegram передал повреждённые данные входа. Откройте приложение заново.',
  INITDATA_BAD_SIGNATURE: 'Подпись Telegram не совпала. Откройте приложение заново.',
  INITDATA_EXPIRED: 'Данные входа устарели. Откройте приложение заново.',
  INITDATA_FUTURE: 'Часы устройства расходятся с сервером. Проверьте дату и время.',
  INITDATA_NO_USER: 'Так приложение не открывается. Запустите его кнопкой меню бота.',
  FORBIDDEN: 'Это действие недоступно.',
  USER_BLOCKED: 'Доступ к каталогу закрыт.',
  NOT_SUBSCRIBED: 'Плеер открыт только для подписчиков канала.',
  MEDIA_TOKEN_MISSING: 'Ссылка на файл потерялась. Нажмите воспроизведение ещё раз.',
  MEDIA_TOKEN_MALFORMED: 'Ссылка на файл повреждена. Нажмите воспроизведение ещё раз.',
  MEDIA_TOKEN_EXPIRED: 'Ссылка на файл истекла. Нажмите воспроизведение ещё раз.',
  MEDIA_TOKEN_BAD_SIG: 'Ссылка на файл не подошла. Нажмите воспроизведение ещё раз.',
  NOT_FOUND: 'Этого больше нет в каталоге.',
  CONFLICT: 'Кто-то уже изменил этот список. Обновите его.',
  TRACK_UNAVAILABLE: 'Запись пропала из канала.',
  RANGE_NOT_SATISFIABLE: 'Файл читается не с того места. Перезапустите трек.',
  SEARCH_EMPTY_QUERY: 'Введите запрос.',
  RATE_LIMITED: 'Слишком много запросов. Подождите немного.',
  INTERNAL: 'Сервер не справился. Попробуйте ещё раз.',
  TELEGRAM_ERROR: 'Telegram не отдал файл. Попробуйте ещё раз.',
  UPSTREAM_THROTTLED: 'Telegram просит подождать. Скоро вернёмся.',
  INDEX_NOT_READY: 'Каталог ещё описывается. Загляните позже.',
  NETWORK_FAILED: 'Нет связи с сервером.',
  TIMEOUT: 'Сервер не ответил вовремя.',
  BAD_RESPONSE: 'Сервер ответил непонятно. Попробуйте ещё раз.',
  ABORTED: 'Запрос отменён.',
  NO_INIT_DATA: 'Так приложение не открывается. Запустите его кнопкой меню бота.',
});

/**
 * Codes worth offering a `ЕЩЁ РАЗ` button for. Everything else is a dead end that a
 * retry cannot fix, and offering one there is a lie about what will happen.
 */
const RETRYABLE: ReadonlySet<ApiFailureCode> = new Set<ApiFailureCode>([
  'INTERNAL',
  'TELEGRAM_ERROR',
  'UPSTREAM_THROTTLED',
  'RATE_LIMITED',
  'INDEX_NOT_READY',
  'NETWORK_FAILED',
  'TIMEOUT',
  'BAD_RESPONSE',
  'RANGE_NOT_SATISFIABLE',
]);

/**
 * The mono catalogue code on the error plate, e.g. `ERR 0x1A`.
 *
 * Derived from the code's fixed index in {@link ALL_FAILURE_CODES}, so it is stable
 * across builds and can be read out over the phone — and it is *derived*, not invented
 * per site, which is what stops two screens printing two codes for one fault.
 */
export function displayCode(code: ApiFailureCode): string {
  const index = ALL_FAILURE_CODES.indexOf(code);
  const value = index < 0 ? 0xff : index;
  return `ERR 0x${value.toString(16).toUpperCase().padStart(2, '0')}`;
}

/** The plain, serialisable shape a store slice keeps and a state component renders. */
export interface ApiFailure {
  readonly code: ApiFailureCode;
  /** The Russian sentence from {@link USER_MESSAGE_RU}. */
  readonly message: string;
  /** `ERR 0x1A`. */
  readonly displayCode: string;
  /** The HTTP status, or `0` for a transport-level failure. */
  readonly status: number;
  readonly retryable: boolean;
  /** `Retry-After`, seconds, when the server sent one. */
  readonly retryAfterS?: number;
  /** The server's `error.details`, or the zod issue list for `BAD_RESPONSE`. */
  readonly details?: unknown;
  /** The server's own English `error.message`, kept for logs — never rendered. */
  readonly serverMessage?: string;
}

export interface ApiErrorInit {
  code: ApiFailureCode;
  status?: number;
  details?: unknown;
  retryAfterS?: number;
  serverMessage?: string;
  cause?: unknown;
}

/**
 * The only thing this module ever throws.
 *
 * `message` is the Russian sentence, so an accidental `String(err)` in a log or a
 * boundary still produces something a human can read; the machine-readable half is
 * `code`.
 */
export class ApiError extends Error {
  readonly code: ApiFailureCode;
  readonly status: number;
  readonly retryable: boolean;
  readonly displayCode: string;
  readonly details: unknown;
  readonly retryAfterS: number | undefined;
  readonly serverMessage: string | undefined;

  constructor(init: ApiErrorInit) {
    super(USER_MESSAGE_RU[init.code], init.cause === undefined ? undefined : { cause: init.cause });
    this.name = 'ApiError';
    this.code = init.code;
    this.status = init.status ?? 0;
    this.retryable = RETRYABLE.has(init.code);
    this.displayCode = displayCode(init.code);
    this.details = init.details;
    this.retryAfterS = init.retryAfterS;
    this.serverMessage = init.serverMessage;
  }

  /** The store-safe projection. No prototype, no stack, JSON round-trips. */
  toFailure(): ApiFailure {
    const failure: {
      -readonly [K in keyof ApiFailure]: ApiFailure[K];
    } = {
      code: this.code,
      message: this.message,
      displayCode: this.displayCode,
      status: this.status,
      retryable: this.retryable,
    };
    if (this.retryAfterS !== undefined) failure.retryAfterS = this.retryAfterS;
    if (this.details !== undefined) failure.details = this.details;
    if (this.serverMessage !== undefined) failure.serverMessage = this.serverMessage;
    return failure;
  }
}

/**
 * Coerce anything caught into a {@link ApiFailure}. Used at every store boundary, so an
 * unexpected `TypeError` from a caller's own code still lands as a renderable failure
 * instead of an empty error plate.
 */
export function toApiFailure(error: unknown): ApiFailure {
  if (error instanceof ApiError) return error.toFailure();
  const wrapped = new ApiError({ code: 'BAD_RESPONSE', cause: error });
  return wrapped.toFailure();
}

/** `true` when the value is an {@link ApiError} carrying exactly `code`. */
export function isApiErrorCode(error: unknown, code: ApiFailureCode): boolean {
  return error instanceof ApiError && error.code === code;
}

/* ────────────────────────────────────────────────────────────────────────────
 * Validation seam
 * ──────────────────────────────────────────────────────────────────────────── */

/**
 * The shape of a `@bl/shared` zod schema, structurally, so this module needs no `zod`
 * import (and `@bl/web` needs no `zod` dependency).
 */
export interface ResponseValidator<T> {
  safeParse(
    value: unknown,
  ): { success: true; data: T; error?: unknown } | { success: false; error: unknown };
}

/**
 * `POST /api/queue/items` is the one endpoint whose response body `spec.md` §6.7 does
 * not pin down — it states the request and nothing else, where its neighbours say "the
 * new queue" and "the new queue header". Rather than guess and make a dev-mode
 * validation failure out of the route author's equally valid reading, the client accepts
 * either and normalises in the store.
 */
function eitherOf<A, B>(
  first: ResponseValidator<A>,
  second: ResponseValidator<B>,
): ResponseValidator<A | B> {
  return {
    safeParse(value: unknown) {
      const a = first.safeParse(value);
      if (a.success) return { success: true, data: a.data };
      const b = second.safeParse(value);
      if (b.success) return { success: true, data: b.data };
      return { success: false, error: [a.error, b.error] };
    },
  };
}

/** Either the full queue or just its header — see {@link eitherOf}. */
export type QueueMutationResponse = QueueResponse | QueueHeader;

const QueueMutationValidator: ResponseValidator<QueueMutationResponse> = eitherOf(
  QueueResponseSchema,
  QueueHeaderSchema,
);

/** For `204 No Content` and the handful of routes whose body carries nothing to check. */
const PASSTHROUGH: ResponseValidator<unknown> = {
  safeParse: (value: unknown) => ({ success: true, data: value }),
};

/* ────────────────────────────────────────────────────────────────────────────
 * Client construction
 * ──────────────────────────────────────────────────────────────────────────── */

export type FetchLike = (input: string, init?: RequestInit) => Promise<Response>;

/** The in-memory session (`spec.md` §9.2). Serialisable; mirrored by `store/session`. */
export interface SessionSnapshot {
  readonly token: string | null;
  /** Unix **seconds**, as the wire sends it. `0` when there is no token. */
  readonly expiresAt: number;
  /** Unix **seconds** at which this token was received; the TTL baseline. */
  readonly issuedAt: number;
  readonly user: SessionUser | null;
  readonly server: ServerInfo | null;
  readonly startParam: string | null;
}

export const EMPTY_SESSION: SessionSnapshot = Object.freeze({
  token: null,
  expiresAt: 0,
  issuedAt: 0,
  user: null,
  server: null,
  startParam: null,
});

export interface ApiClientDeps {
  /** Same origin by default: `spec.md` §13.1 forbids a second origin for anything. */
  baseUrl?: string;
  fetch?: FetchLike;
  /** Epoch milliseconds. Injected so the refresh threshold is testable. */
  now?: () => number;
  /** The raw, untouched `Telegram.WebApp.initData` (§9.1, rule 3). */
  initData?: () => string;
  platform?: () => string;
  /** Run the zod response schemas. Defaults to `import.meta.env.DEV`. */
  validate?: boolean;
  /** Per-request deadline. `0` disables it. */
  timeoutMs?: number;
  /** Fraction of the TTL after which a refresh is issued (§9.2: 80%). */
  refreshRatio?: number;
}

export interface RequestOptions {
  signal?: AbortSignal | undefined;
  /** `false` for `POST /api/auth/telegram`, which *is* the authentication. */
  auth?: boolean;
  timeoutMs?: number;
}

/**
 * Everything that may appear in a querystring. Deliberately scalar-only: the one array
 * parameter in the whole API (`?groups=` on `/api/search`) is a comma list on the wire
 * and is joined by its own method, so {@link buildQuery} never has to guess a separator.
 */
type QueryScalars = Readonly<Record<string, string | number | boolean | undefined>>;

/** `GET /api/catalogue` — `spec.md` §6.4. */
export type CatalogueArgs = {
  sort?: CatalogueSort;
  seed?: number;
  limit?: number;
  cursor?: string;
  year?: number;
  artistId?: number;
  tagId?: number;
  minDuration?: number;
  maxDuration?: number;
  hasCover?: boolean;
};

/** `GET /api/artists` and `GET /api/tags` — the drawer index sources. */
export type IndexArgs = { limit?: number; cursor?: string; sort?: IndexSort; q?: string };

/** The nested `tracks` page of an artist, tag or playlist detail response. */
export type NestedArgs = { limit?: number; cursor?: string };

/** `GET /api/favorites`. */
export type FavoritesArgs = { sort?: LibrarySort; seed?: number; limit?: number; cursor?: string };

/** `GET /api/history`. */
export type HistoryArgs = { limit?: number; cursor?: string };

/** `GET /api/search`. `groups` is joined into the comma list the route parses. */
export type SearchArgs = {
  q: string;
  limit?: number;
  cursor?: string;
  groups?: readonly SearchGroupName[];
};

/** Per-request deadline. Long enough for a cold Telegram WebView on mobile data. */
const DEFAULT_TIMEOUT_MS = 15_000;

/** §9.2: "The client refreshes at 80% of TTL." */
const DEFAULT_REFRESH_RATIO = 0.8;

/**
 * How long before a media URL's `exp` it stops being handed out.
 *
 * A URL minted with 6 h of life is useless if it expires two minutes into the track it
 * was minted for, and re-minting mid-track breaks byte-stability. Five minutes of slack
 * costs nothing (the token is stateless) and keeps the swap on `ended` free of network.
 */
const MEDIA_URL_SAFETY_S = 300;

/** §6.3: `trackIds` is 1..50 per media-token request. */
const MEDIA_TOKEN_BATCH_MAX = 50;

interface MediaUrlEntry {
  readonly url: string;
  /** Unix seconds. */
  readonly expiresAt: number;
}

export interface ApiClient {
  /* — session — */
  getSession(): SessionSnapshot;
  /** Subscribe to session changes. `store/session` is the only expected subscriber. */
  onSession(listener: (session: SessionSnapshot) => void): () => void;
  /** Exchange `initData` for a session. Single-flight: concurrent callers share one call. */
  authenticate(options?: { force?: boolean }): Promise<AuthTelegramResponse>;
  refresh(): Promise<AuthRefreshResponse>;
  /** Refresh if the clock is past 80% of TTL; authenticate if there is no token at all. */
  ensureSession(): Promise<SessionSnapshot>;
  /** Drop the token and every minted media URL. Called on `USER_BLOCKED` and on logout. */
  clearSession(): void;

  /* — media URLs (§9.4, §9.5, §10.8) — */
  ensureMediaUrls(
    trackIds: readonly number[],
    kind?: MediaTokenKind,
  ): Promise<Record<number, string>>;
  /** The cached, byte-stable URL, or `null`. Never issues a request. */
  mediaUrl(trackId: number, kind?: MediaTokenKind): string | null;
  /** Forget cached URLs (all of them, or one track's). */
  forgetMediaUrls(trackId?: number): void;

  /* — catalogue — */
  catalogue(query?: CatalogueArgs, options?: RequestOptions): Promise<CatalogueResponse>;
  track(ref: number | string, options?: RequestOptions): Promise<TrackDetail>;
  peaks(trackId: number, options?: RequestOptions): Promise<TrackPeaksResponse>;
  artists(query?: IndexArgs, options?: RequestOptions): Promise<ArtistsResponse>;
  artist(id: number, query?: NestedArgs, options?: RequestOptions): Promise<ArtistDetailResponse>;
  tags(query?: IndexArgs, options?: RequestOptions): Promise<TagsResponse>;
  tag(id: number, query?: NestedArgs, options?: RequestOptions): Promise<TagDetailResponse>;

  /* — search — */
  search(query: SearchArgs, options?: RequestOptions): Promise<SearchResponse>;
  suggest(q: string, options?: RequestOptions): Promise<SuggestResponse>;

  /* — library — */
  favorites(query?: FavoritesArgs, options?: RequestOptions): Promise<FavoritesResponse>;
  addFavorite(trackId: number, options?: RequestOptions): Promise<FavoriteAddedResponse>;
  removeFavorite(trackId: number, options?: RequestOptions): Promise<FavoriteRemovedResponse>;
  playlists(options?: RequestOptions): Promise<PlaylistsResponse>;
  createPlaylist(
    body: PlaylistCreateRequest,
    options?: RequestOptions,
  ): Promise<PlaylistCreateResponse>;
  playlist(
    id: number,
    query?: NestedArgs,
    options?: RequestOptions,
  ): Promise<PlaylistDetailResponse>;
  patchPlaylist(
    id: number,
    body: PlaylistPatchRequest,
    options?: RequestOptions,
  ): Promise<PlaylistPatchResponse>;
  deletePlaylist(id: number, options?: RequestOptions): Promise<void>;
  addPlaylistTracks(
    id: number,
    body: PlaylistAddTracksRequest,
    options?: RequestOptions,
  ): Promise<PlaylistAddTracksResponse>;
  removePlaylistTrack(id: number, trackId: number, options?: RequestOptions): Promise<Ack>;
  reorderPlaylist(
    id: number,
    body: PlaylistReorderRequest,
    options?: RequestOptions,
  ): Promise<PlaylistReorderResponse>;
  history(query?: HistoryArgs, options?: RequestOptions): Promise<HistoryResponse>;

  /* — playback — */
  playback(options?: RequestOptions): Promise<PlaybackResponse>;
  putPlayback(body: PlaybackPutRequest, options?: RequestOptions): Promise<PlaybackPutResponse>;
  recordPlay(body: PlayCreateRequest, options?: RequestOptions): Promise<PlayCreateResponse>;

  /* — queue — */
  queue(options?: RequestOptions): Promise<QueueResponse>;
  replaceQueue(body: QueueReplaceRequest, options?: RequestOptions): Promise<QueueResponse>;
  addQueueItems(
    body: QueueAddItemsRequest,
    options?: RequestOptions,
  ): Promise<QueueMutationResponse>;
  removeQueueItem(itemId: number, options?: RequestOptions): Promise<QueueHeader>;
  reorderQueue(body: QueueReorderRequest, options?: RequestOptions): Promise<QueueHeader>;
  clearQueue(options?: RequestOptions): Promise<void>;

  /* — pads (§9.9) — */
  /**
   * The whole CC0 sound library, in one payload.
   *
   * Deliberately not paginated: seventy-odd rows of short metadata is smaller than one
   * cursor round trip, and the library screen is a single list the user scrolls. The
   * audio itself is fetched per file from {@link padSoundUrl}.
   */
  padSounds(options?: RequestOptions): Promise<PadSoundsResponse>;
  /** This account's bank, or the factory bank when it has never been saved. */
  padKit(options?: RequestOptions): Promise<PadKitResponse>;
  /** Replace all sixteen slots. There is no partial update — see `PadKitPutRequestSchema`. */
  putPadKit(body: PadKitPutRequest, options?: RequestOptions): Promise<PadKitResponse>;
  /**
   * Where one library sound lives. A plain same-origin path, **not** a media token:
   * `/media/pads/*` is a small immutable file shipped in the image, not a byte-range
   * stream out of Telegram, so none of {@link ensureMediaUrls}' machinery applies.
   */
  padSoundUrl(file: string): string;

  /* — share — */
  shareTrack(body: ShareTrackRequest, options?: RequestOptions): Promise<ShareTrackResponse>;
}

/* ────────────────────────────────────────────────────────────────────────────
 * Implementation
 * ──────────────────────────────────────────────────────────────────────────── */

function buildQuery(query: QueryScalars | undefined): string {
  if (query === undefined) return '';
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value === undefined) continue;
    params.set(key, typeof value === 'string' ? value : String(value));
  }
  const encoded = params.toString();
  return encoded.length > 0 ? `?${encoded}` : '';
}

/** `?groups=tracks,artists` — the only list-valued parameter in the API. */
function searchQuery(args: SearchArgs): QueryScalars {
  const query: { q: string; limit?: number; cursor?: string; groups?: string } = { q: args.q };
  if (args.limit !== undefined) query.limit = args.limit;
  if (args.cursor !== undefined) query.cursor = args.cursor;
  if (args.groups !== undefined && args.groups.length > 0) query.groups = args.groups.join(',');
  return query;
}

/** `Retry-After` is either delta-seconds or an HTTP date; only the former is useful. */
function parseRetryAfter(header: string | null): number | undefined {
  if (header === null) return undefined;
  const seconds = Number.parseInt(header, 10);
  return Number.isFinite(seconds) && seconds >= 0 ? seconds : undefined;
}

/**
 * `AbortSignal.timeout` and `AbortSignal.any` are deliberately not used: jsdom's
 * coverage of both is partial, and a hand-rolled controller is what makes the deadline
 * assertable under fake timers.
 */
function withDeadline(
  timeoutMs: number,
  external: AbortSignal | undefined,
): { signal: AbortSignal; dispose: () => void; timedOut: () => boolean } {
  const controller = new AbortController();
  let expired = false;

  const onExternalAbort = (): void => {
    controller.abort();
  };
  if (external !== undefined) {
    if (external.aborted) controller.abort();
    else external.addEventListener('abort', onExternalAbort, { once: true });
  }

  const timer =
    timeoutMs > 0
      ? setTimeout(() => {
          expired = true;
          controller.abort();
        }, timeoutMs)
      : undefined;

  return {
    signal: controller.signal,
    dispose: () => {
      if (timer !== undefined) clearTimeout(timer);
      if (external !== undefined) external.removeEventListener('abort', onExternalAbort);
    },
    timedOut: () => expired,
  };
}

export function createApiClient(deps: ApiClientDeps = {}): ApiClient {
  const baseUrl = deps.baseUrl ?? '';
  const doFetch: FetchLike = deps.fetch ?? ((input, init) => globalThis.fetch(input, init));
  const now = deps.now ?? (() => Date.now());
  const readInitData = deps.initData ?? (() => telegram.initData);
  const readPlatform = deps.platform ?? (() => telegram.platform);
  const validate = deps.validate ?? Boolean(import.meta.env.DEV);
  const defaultTimeoutMs = deps.timeoutMs ?? DEFAULT_TIMEOUT_MS;
  const refreshRatio = deps.refreshRatio ?? DEFAULT_REFRESH_RATIO;

  let session: SessionSnapshot = EMPTY_SESSION;
  const sessionListeners = new Set<(snapshot: SessionSnapshot) => void>();

  /** `${kind}:${trackId}` → the exact minted string. */
  const mediaUrls = new Map<string, MediaUrlEntry>();

  let authInFlight: Promise<AuthTelegramResponse> | null = null;
  let refreshInFlight: Promise<AuthRefreshResponse> | null = null;

  const nowSeconds = (): number => Math.floor(now() / 1000);

  function publishSession(next: SessionSnapshot): void {
    session = next;
    for (const listener of sessionListeners) listener(session);
  }

  function adoptSession(payload: AuthTelegramResponse | AuthRefreshResponse): void {
    publishSession({
      token: payload.token,
      expiresAt: payload.expiresAt,
      issuedAt: nowSeconds(),
      user: payload.user,
      server: payload.server,
      startParam: 'startParam' in payload ? payload.startParam : session.startParam,
    });
  }

  /** The instant at which a still-valid token should be traded for a fresh one. */
  function refreshDueAt(snapshot: SessionSnapshot): number {
    const ttl = snapshot.expiresAt - snapshot.issuedAt;
    if (ttl <= 0) return snapshot.expiresAt;
    return snapshot.issuedAt + Math.floor(ttl * refreshRatio);
  }

  function parseBody<T>(validator: ResponseValidator<T>, data: unknown, always: boolean): T {
    if (!validate && !always) return data as T;
    const result = validator.safeParse(data);
    if (result.success) return result.data;
    throw new ApiError({ code: 'BAD_RESPONSE', status: 200, details: result.error });
  }

  /** Turn a non-2xx (or an envelope with `ok: false`) into an {@link ApiError}. */
  function toHttpError(status: number, body: unknown, retryAfterS: number | undefined): ApiError {
    const parsed = ErrSchema.safeParse(body);
    if (parsed.success) {
      const init: ApiErrorInit = {
        code: parsed.data.error.code,
        status,
        serverMessage: parsed.data.error.message,
      };
      if (parsed.data.error.details !== undefined) init.details = parsed.data.error.details;
      if (retryAfterS !== undefined) init.retryAfterS = retryAfterS;
      return new ApiError(init);
    }
    // A body we cannot read is not a server code we can act on — but the status still
    // tells the user something true.
    const code: ApiFailureCode = status === 401 ? 'NO_SESSION' : 'BAD_RESPONSE';
    const init: ApiErrorInit = { code, status, details: body };
    if (retryAfterS !== undefined) init.retryAfterS = retryAfterS;
    return new ApiError(init);
  }

  async function readEnvelope(
    response: Response,
  ): Promise<{ ok: true; data: unknown } | { ok: false; error: ApiError }> {
    const retryAfterS = parseRetryAfter(response.headers.get('retry-after'));

    if (response.status === 204) return { ok: true, data: undefined };

    let body: unknown;
    try {
      body = await (response.json() as Promise<unknown>);
    } catch (cause) {
      if (response.ok) {
        return {
          ok: false,
          error: new ApiError({ code: 'BAD_RESPONSE', status: response.status, cause }),
        };
      }
      return { ok: false, error: toHttpError(response.status, undefined, retryAfterS) };
    }

    if (!response.ok) return { ok: false, error: toHttpError(response.status, body, retryAfterS) };

    if (
      typeof body === 'object' &&
      body !== null &&
      'ok' in body &&
      body.ok === true &&
      'data' in body
    ) {
      return { ok: true, data: (body as { data: unknown }).data };
    }
    // `200` carrying `{ ok: false }` is legal for the routes that report a soft failure.
    return { ok: false, error: toHttpError(response.status, body, retryAfterS) };
  }

  async function send(
    method: string,
    path: string,
    init: { body?: unknown; token?: string | null; options?: RequestOptions },
  ): Promise<unknown> {
    const options = init.options ?? {};
    const timeoutMs = options.timeoutMs ?? defaultTimeoutMs;
    const deadline = withDeadline(timeoutMs, options.signal);

    const headers: Record<string, string> = { accept: 'application/json' };
    if (init.body !== undefined) headers['content-type'] = 'application/json; charset=utf-8';
    if (init.token !== undefined && init.token !== null) {
      headers['authorization'] = `Bearer ${init.token}`;
    }

    const request: RequestInit = {
      method,
      headers,
      // §9.3: no cookies anywhere. Omitting credentials is what makes that structural
      // rather than a convention nobody re-checks.
      credentials: 'omit',
      cache: 'no-store',
      signal: deadline.signal,
    };
    if (init.body !== undefined) request.body = JSON.stringify(init.body);

    let response: Response;
    try {
      response = await doFetch(`${baseUrl}${path}`, request);
    } catch (cause) {
      if (deadline.timedOut()) throw new ApiError({ code: 'TIMEOUT', cause });
      if (options.signal?.aborted === true) throw new ApiError({ code: 'ABORTED', cause });
      throw new ApiError({ code: 'NETWORK_FAILED', cause });
    } finally {
      deadline.dispose();
    }

    const envelope = await readEnvelope(response);
    if (!envelope.ok) throw envelope.error;
    return envelope.data;
  }

  /* — session lifecycle — */

  async function runAuthenticate(): Promise<AuthTelegramResponse> {
    const initData = readInitData();
    if (initData.length === 0) throw new ApiError({ code: 'NO_INIT_DATA' });

    const platform = readPlatform();
    const body: { initData: string; platform?: string } = { initData };
    if (platform.length > 0) body.platform = platform;

    const data = await send('POST', '/api/auth/telegram', { body, options: { auth: false } });
    const payload = parseBody(AuthTelegramResponseSchema, data, true);
    adoptSession(payload);
    return payload;
  }

  function authenticate(options?: { force?: boolean }): Promise<AuthTelegramResponse> {
    if (authInFlight !== null) return authInFlight;
    if (options?.force === true) mediaUrls.clear();
    const pending = runAuthenticate().finally(() => {
      authInFlight = null;
    });
    authInFlight = pending;
    return pending;
  }

  async function runRefresh(token: string): Promise<AuthRefreshResponse> {
    const data = await send('POST', '/api/auth/refresh', { token });
    const payload = parseBody(AuthRefreshResponseSchema, data, true);
    adoptSession(payload);
    return payload;
  }

  function refresh(): Promise<AuthRefreshResponse> {
    if (refreshInFlight !== null) return refreshInFlight;
    const token = session.token;
    if (token === null) {
      return Promise.reject(new ApiError({ code: 'NO_SESSION' }));
    }
    const pending = runRefresh(token).finally(() => {
      refreshInFlight = null;
    });
    refreshInFlight = pending;
    return pending;
  }

  async function ensureSession(): Promise<SessionSnapshot> {
    if (session.token === null) {
      await authenticate();
      return session;
    }
    if (nowSeconds() < refreshDueAt(session)) return session;
    try {
      await refresh();
    } catch (error) {
      // A refresh refused for a dead token is exactly the case §6.3 says to answer by
      // re-exchanging `initData`. Anything else (offline, 500) is the caller's problem.
      if (isApiErrorCode(error, 'SESSION_EXPIRED') || isApiErrorCode(error, 'BAD_SESSION')) {
        await authenticate({ force: true });
        return session;
      }
      throw error;
    }
    return session;
  }

  /**
   * The authenticated request path: freshen the session, send, and on `SESSION_EXPIRED`
   * re-exchange `initData` **once** and replay the request with the new token. A second
   * failure is surfaced untouched.
   */
  async function authed<T>(
    method: string,
    path: string,
    validator: ResponseValidator<T>,
    init: { body?: unknown; options?: RequestOptions } = {},
  ): Promise<T> {
    const options = init.options;
    const snapshot = await ensureSession();

    const attempt = async (token: string | null): Promise<unknown> => {
      const args: { body?: unknown; token: string | null; options?: RequestOptions } = { token };
      if (init.body !== undefined) args.body = init.body;
      if (options !== undefined) args.options = options;
      return send(method, path, args);
    };

    /*
     * Never leave without a credential. `send()` omits the header entirely for a null
     * token, so a snapshot that somehow arrives empty used to produce an anonymous
     * request and a `401` the caller could only report — which is what a live session
     * did to `POST /api/share/track` on 2026-09-05. One forced re-exchange is cheap;
     * a request that cannot possibly succeed is not.
     */
    const token = snapshot.token ?? (await authenticate({ force: true })).token;

    try {
      return parseBody(validator, await attempt(token), false);
    } catch (error) {
      /*
       * `NO_SESSION` alongside `SESSION_EXPIRED`: to this layer both mean "what you
       * sent is not a usable credential", and §6.3's answer to that is to re-exchange
       * `initData`. Retrying only the expiry case left a lost token fatal. Still
       * exactly ONE retry — the replay's own failure is surfaced untouched, so neither
       * code can loop.
       */
      const recoverable =
        isApiErrorCode(error, 'SESSION_EXPIRED') || isApiErrorCode(error, 'NO_SESSION');
      if (!recoverable) throw error;
      const reissued = await authenticate({ force: true });
      return parseBody(validator, await attempt(reissued.token), false);
    }
  }

  /** The `204`/no-body variant. Nothing to validate, so nothing is. */
  async function authedVoid(
    method: string,
    path: string,
    init: { body?: unknown; options?: RequestOptions } = {},
  ): Promise<void> {
    await authed(method, path, PASSTHROUGH, init);
  }

  /* — media URLs — */

  const mediaKey = (trackId: number, kind: MediaTokenKind): string => `${kind}:${trackId}`;

  function cachedMediaUrl(trackId: number, kind: MediaTokenKind): string | null {
    const entry = mediaUrls.get(mediaKey(trackId, kind));
    if (entry === undefined) return null;
    return entry.expiresAt - MEDIA_URL_SAFETY_S > nowSeconds() ? entry.url : null;
  }

  async function mintMediaUrls(
    trackIds: readonly number[],
    kind: MediaTokenKind,
  ): Promise<MediaTokenResponse> {
    const body = { trackIds: [...trackIds], kind };
    const payload = await authed('POST', '/api/auth/media-token', MediaTokenResponseSchema, {
      body,
    });
    for (const [key, url] of Object.entries(payload.urls)) {
      const id = Number.parseInt(key, 10);
      if (!Number.isSafeInteger(id)) continue;
      mediaUrls.set(mediaKey(id, kind), { url, expiresAt: payload.expiresAt });
    }
    return payload;
  }

  async function ensureMediaUrls(
    trackIds: readonly number[],
    kind: MediaTokenKind = 'audio',
  ): Promise<Record<number, string>> {
    // De-duplicated and order-preserving: §9.5 mints for the current AND the next track
    // in one call, and the pair is frequently the same track twice at the end of a queue.
    const wanted: number[] = [];
    for (const id of trackIds) {
      if (Number.isSafeInteger(id) && id > 0 && !wanted.includes(id)) wanted.push(id);
    }

    const resolved: Record<number, string> = {};
    const missing: number[] = [];
    for (const id of wanted) {
      const cached = cachedMediaUrl(id, kind);
      // A cached URL is returned VERBATIM. Re-minting would produce a different `exp`,
      // a different signature and therefore a different URL — and the prefetch that
      // already warmed the old one would have warmed nothing (§10.8, requirement 1).
      if (cached !== null) resolved[id] = cached;
      else missing.push(id);
    }

    for (let i = 0; i < missing.length; i += MEDIA_TOKEN_BATCH_MAX) {
      const batch = missing.slice(i, i + MEDIA_TOKEN_BATCH_MAX);
      if (batch.length === 0) continue;
      await mintMediaUrls(batch, kind);
      for (const id of batch) {
        const minted = cachedMediaUrl(id, kind);
        // Absent means the server omitted the id: unknown or soft-deleted (§6.3). The
        // key is omitted here too rather than mapped to `null` or to an invented URL.
        if (minted !== null) resolved[id] = minted;
      }
    }

    return resolved;
  }

  /* — the endpoint surface — */

  const client: ApiClient = {
    getSession: () => session,
    onSession(listener) {
      sessionListeners.add(listener);
      return () => {
        sessionListeners.delete(listener);
      };
    },
    authenticate,
    refresh,
    ensureSession,
    clearSession() {
      mediaUrls.clear();
      authInFlight = null;
      refreshInFlight = null;
      publishSession(EMPTY_SESSION);
    },

    ensureMediaUrls,
    mediaUrl: (trackId, kind = 'audio') => cachedMediaUrl(trackId, kind),
    forgetMediaUrls(trackId) {
      if (trackId === undefined) {
        mediaUrls.clear();
        return;
      }
      for (const key of [...mediaUrls.keys()]) {
        if (key.endsWith(`:${trackId}`)) mediaUrls.delete(key);
      }
    },

    catalogue: (query, options) =>
      authed('GET', `/api/catalogue${buildQuery(query)}`, CatalogueResponseSchema, {
        ...(options === undefined ? {} : { options }),
      }),
    track: (ref, options) =>
      authed('GET', `/api/tracks/${encodeURIComponent(String(ref))}`, TrackDetailSchema, {
        ...(options === undefined ? {} : { options }),
      }),
    peaks: (trackId, options) =>
      authed('GET', `/api/tracks/${trackId}/peaks`, TrackPeaksResponseSchema, {
        ...(options === undefined ? {} : { options }),
      }),
    artists: (query, options) =>
      authed('GET', `/api/artists${buildQuery(query)}`, ArtistsResponseSchema, {
        ...(options === undefined ? {} : { options }),
      }),
    artist: (id, query, options) =>
      authed('GET', `/api/artists/${id}${buildQuery(query)}`, ArtistDetailResponseSchema, {
        ...(options === undefined ? {} : { options }),
      }),
    tags: (query, options) =>
      authed('GET', `/api/tags${buildQuery(query)}`, TagsResponseSchema, {
        ...(options === undefined ? {} : { options }),
      }),
    tag: (id, query, options) =>
      authed('GET', `/api/tags/${id}${buildQuery(query)}`, TagDetailResponseSchema, {
        ...(options === undefined ? {} : { options }),
      }),

    search: (query, options) =>
      authed('GET', `/api/search${buildQuery(searchQuery(query))}`, SearchResponseSchema, {
        ...(options === undefined ? {} : { options }),
      }),
    suggest: (q, options) =>
      authed('GET', `/api/search/suggest${buildQuery({ q })}`, SuggestResponseSchema, {
        ...(options === undefined ? {} : { options }),
      }),

    favorites: (query, options) =>
      authed('GET', `/api/favorites${buildQuery(query)}`, FavoritesResponseSchema, {
        ...(options === undefined ? {} : { options }),
      }),
    addFavorite: (trackId, options) =>
      authed('PUT', `/api/favorites/${trackId}`, FavoriteAddedResponseSchema, {
        ...(options === undefined ? {} : { options }),
      }),
    removeFavorite: (trackId, options) =>
      authed('DELETE', `/api/favorites/${trackId}`, FavoriteRemovedResponseSchema, {
        ...(options === undefined ? {} : { options }),
      }),
    playlists: (options) =>
      authed('GET', '/api/playlists', PlaylistsResponseSchema, {
        ...(options === undefined ? {} : { options }),
      }),
    createPlaylist: (body, options) =>
      authed('POST', '/api/playlists', PlaylistCreateResponseSchema, {
        body,
        ...(options === undefined ? {} : { options }),
      }),
    playlist: (id, query, options) =>
      authed('GET', `/api/playlists/${id}${buildQuery(query)}`, PlaylistDetailResponseSchema, {
        ...(options === undefined ? {} : { options }),
      }),
    patchPlaylist: (id, body, options) =>
      authed('PATCH', `/api/playlists/${id}`, PlaylistPatchResponseSchema, {
        body,
        ...(options === undefined ? {} : { options }),
      }),
    deletePlaylist: (id, options) =>
      authedVoid('DELETE', `/api/playlists/${id}`, {
        ...(options === undefined ? {} : { options }),
      }),
    addPlaylistTracks: (id, body, options) =>
      authed('POST', `/api/playlists/${id}/tracks`, PlaylistAddTracksResponseSchema, {
        body,
        ...(options === undefined ? {} : { options }),
      }),
    removePlaylistTrack: (id, trackId, options) =>
      authed(
        'DELETE',
        `/api/playlists/${id}/tracks/${trackId}`,
        PlaylistRemoveTrackResponseSchema,
        {
          ...(options === undefined ? {} : { options }),
        },
      ),
    reorderPlaylist: (id, body, options) =>
      authed('POST', `/api/playlists/${id}/reorder`, PlaylistReorderResponseSchema, {
        body,
        ...(options === undefined ? {} : { options }),
      }),
    history: (query, options) =>
      authed('GET', `/api/history${buildQuery(query)}`, HistoryResponseSchema, {
        ...(options === undefined ? {} : { options }),
      }),

    playback: (options) =>
      authed('GET', '/api/playback', PlaybackResponseSchema, {
        ...(options === undefined ? {} : { options }),
      }),
    putPlayback: (body, options) =>
      authed('PUT', '/api/playback', PlaybackPutResponseSchema, {
        body,
        ...(options === undefined ? {} : { options }),
      }),
    recordPlay: (body, options) =>
      authed('POST', '/api/plays', PlayCreateResponseSchema, {
        body,
        ...(options === undefined ? {} : { options }),
      }),

    padSounds: (options) =>
      authed('GET', '/api/pads/sounds', PadSoundsResponseSchema, {
        ...(options === undefined ? {} : { options }),
      }),
    padKit: (options) =>
      authed('GET', '/api/pads/kit', PadKitResponseSchema, {
        ...(options === undefined ? {} : { options }),
      }),
    putPadKit: (body, options) =>
      authed('PUT', '/api/pads/kit', PadKitResponseSchema, {
        body,
        ...(options === undefined ? {} : { options }),
      }),
    padSoundUrl: (file) => `${baseUrl}${PAD_MEDIA_PREFIX}${encodeURIComponent(file)}`,
    queue: (options) =>
      authed('GET', '/api/queue', QueueResponseSchema, {
        ...(options === undefined ? {} : { options }),
      }),
    replaceQueue: (body, options) =>
      authed('PUT', '/api/queue', QueueResponseSchema, {
        body,
        ...(options === undefined ? {} : { options }),
      }),
    addQueueItems: (body, options) =>
      authed('POST', '/api/queue/items', QueueMutationValidator, {
        body,
        ...(options === undefined ? {} : { options }),
      }),
    removeQueueItem: (itemId, options) =>
      authed('DELETE', `/api/queue/items/${itemId}`, QueueHeaderSchema, {
        ...(options === undefined ? {} : { options }),
      }),
    reorderQueue: (body, options) =>
      authed('POST', '/api/queue/reorder', QueueHeaderSchema, {
        body,
        ...(options === undefined ? {} : { options }),
      }),
    clearQueue: (options) =>
      authedVoid('DELETE', '/api/queue', { ...(options === undefined ? {} : { options }) }),

    shareTrack: (body, options) =>
      authed('POST', '/api/share/track', ShareTrackResponseSchema, {
        body,
        ...(options === undefined ? {} : { options }),
      }),
  };

  return client;
}

/**
 * The app-wide client. One instance, because the session token and the media-URL cache
 * are per-app state: a second client would mint a second set of URLs and halve the
 * prefetch hit rate.
 */
export const api: ApiClient = createApiClient();
